import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(ScrollTrigger, useGSAP);

/**
 * Filas en loop infinito que reaccionan al scroll: la velocidad del scroll las
 * acelera y su sentido las invierte; al soltar vuelven a la velocidad base en
 * ese mismo sentido. Cada fila (`selector`, dentro de `scopeRef`) lleva su
 * contenido duplicado y el loop la desplaza -50%; las filas impares van al revés.
 *
 * Uso:
 *   const ref = useRef(null);
 *   useScrollMarquee(ref, '.mi-fila');
 *   <section ref={ref}><div className="mi-fila flex w-max">{[...items, ...items]}</div></section>
 *
 * `dependencias` rearma las filas cuando cambia su contenido (p. ej. al llegar
 * los datos de Sanity después del primer render).
 */
export default function useScrollMarquee(scopeRef, selector, { duration = 40, dependencias = [] } = {}) {
    useGSAP(() => {
        const scope = scopeRef.current;

        // El totalTime adelantado deja margen para correrlas en reversa
        // (si no, se frenan al llegar a 0)
        const filas = gsap.utils.toArray(selector, scope).map((track, i) => {
            const tween = gsap.fromTo(track,
                { xPercent: i % 2 ? -50 : 0 },
                { xPercent: i % 2 ? 0 : -50, duration, ease: 'none', repeat: -1 });
            tween.totalTime(tween.duration() * 1000);
            return tween;
        });

        let reposo;
        const velocidad = (timeScale, dur, ease) => filas.forEach((t) => gsap.to(t, { timeScale, duration: dur, ease, overwrite: true }));
        ScrollTrigger.create({
            trigger: scope,
            start: 'top bottom',
            end: 'bottom top',
            onUpdate: (self) => {
                const impulso = 1 + Math.min(Math.abs(self.getVelocity()) / 200, 8);
                velocidad(self.direction * impulso, 0.25, 'power1.out');
                clearTimeout(reposo);
                reposo = setTimeout(() => velocidad(self.direction, 1.2, 'power2.out'), 120);
            },
        });

        return () => clearTimeout(reposo);
    }, { scope: scopeRef, dependencies: [selector, duration, ...dependencias] });
}
